<template>
  <div v-if="visible" class="fixed inset-0 z-[999999999] overflow-y-auto">
    <!-- Backdrop overlay -->
    <div class="fixed inset-0 bg-black bg-opacity-75" @click="close"></div>

    <!-- Modal panel -->
    <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0 relative">
      <div class="relative transform overflow-hidden rounded-xl bg-gray-900 text-left shadow-2xl transition-all sm:my-8 w-full max-w-full sm:max-w-2xl mx-4">
        <!-- Header -->
        <div class="bg-gray-800 px-6 py-5 border-b border-gray-700 sm:px-6">
          <div class="flex items-center justify-between">
            <h3 class="text-xl font-semibold text-white">
              Přizpůsobit nastavení cookies
            </h3>
            <button type="button" @click="close" 
              class="rounded-md p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 focus:outline-none">
              <span class="sr-only">Zavřít</span>
              <!-- X icon -->
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Modal body -->
        <div class="bg-gray-900 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <!-- Cookie preference items -->
          <div class="mb-8 pb-6 border-b border-gray-700">
            <div class="flex justify-between items-start mb-3">
              <h4 class="text-base font-semibold text-white">Technické a tedy nezbytné cookies</h4>
              <div class="bg-green-900 text-green-200 text-xs font-medium px-3 py-1 rounded-full">Povolené</div>
            </div>
            <p class="text-sm text-gray-300 mb-4 leading-relaxed">
              Jsou nutnou podmínkou pro fungování našeho webu. Díky nim procházíte jednotlivými stránkami, vyhledáváte nebo se přihlašujete.
            </p>
            
            <div class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="text-sm font-medium text-gray-200">Technické cookies</div>
              </div>
              <div class="relative inline-block">
                <label class="flex items-center cursor-not-allowed opacity-70">
                  <div class="relative">
                    <input type="checkbox" class="sr-only" checked disabled />
                    <div class="block w-12 h-6 rounded-full bg-green-600"></div>
                    <div class="absolute left-6 top-1 w-4 h-4 rounded-full transform bg-white"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <div class="mb-8 pb-6 border-b border-gray-700">
            <div class="flex justify-between items-start mb-3">
              <h4 class="text-base font-semibold text-white">Statistické cookies</h4>
              <div 
                :class="isStatisticsEnabled ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'" 
                class="text-xs font-medium px-3 py-1 rounded-full"
              >
                {{ isStatisticsEnabled ? 'Povolené' : 'Zakázané' }}
              </div>
            </div>
            <p class="text-sm text-gray-300 mb-4 leading-relaxed">
              Díky těmto cookies víme, které předpisy a dokumenty jsou u nás navštěvované. To nám umožňuje dávat Vám doporučení „na míru".
              Součástí jsou i cookies třetích stran, jejichž vydavatelem je např. Google.
            </p>
            
            <div class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="text-sm font-medium text-gray-200">Statistické cookies</div>
              </div>
              <div class="relative inline-block">
                <label class="flex items-center cursor-pointer">
                  <div class="relative">
                    <input 
                      v-model="statisticsEnabled"
                      type="checkbox" 
                      class="sr-only" 
                    />
                    <div :class="[
                      'block w-12 h-6 rounded-full transition-colors duration-200',
                      statisticsEnabled ? 'bg-green-600' : 'bg-gray-600'
                    ]"></div>
                    <div :class="[
                      'absolute left-1 top-1 w-4 h-4 rounded-full transition duration-200 transform bg-white',
                      statisticsEnabled ? 'translate-x-5' : 'translate-x-0'
                    ]"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <div class="mb-6">
            <div class="flex justify-between items-start mb-3">
              <h4 class="text-base font-semibold text-white">Marketingové cookies</h4>
              <div 
                :class="isMarketingEnabled ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'" 
                class="text-xs font-medium px-3 py-1 rounded-full"
              >
                {{ isMarketingEnabled ? 'Povolené' : 'Zakázané' }}
              </div>
            </div>
            <p class="text-sm text-gray-300 mb-4 leading-relaxed">
              Tyto cookies sledují Váš pohyb na webu. Slouží zejména k zobrazování relevantních reklam a k využítí Vašich sociálních sítí.
              Rovněž zde to mohou být cookies třetích stran, jejichž vydavatelem jsou instituce jako Google, Facebook a další.
            </p>
            
            <div class="flex items-center justify-between py-3">
              <div class="flex items-center">
                <div class="text-sm font-medium text-gray-200">Marketingové cookies</div>
              </div>
              <div class="relative inline-block">
                <label class="flex items-center cursor-pointer">
                  <div class="relative">
                    <input 
                      v-model="marketingEnabled"
                      type="checkbox" 
                      class="sr-only" 
                    />
                    <div :class="[
                      'block w-12 h-6 rounded-full transition-colors duration-200',
                      marketingEnabled ? 'bg-green-600' : 'bg-gray-600'
                    ]"></div>
                    <div :class="[
                      'absolute left-1 top-1 w-4 h-4 rounded-full transition duration-200 transform bg-white',
                      marketingEnabled ? 'translate-x-5' : 'translate-x-0'
                    ]"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-800 px-6 py-5 sm:flex sm:flex-row-reverse sm:px-6">
          <button @click="acceptAll" 
            class="inline-flex w-full justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 sm:ml-3 sm:w-auto">
            Přijmout vše
          </button>
          <button @click="savePreferences" 
            class="mt-3 inline-flex w-full justify-center rounded-lg bg-gray-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 sm:mt-0 sm:w-auto">
            Uložit moje nastavení cookies
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CookiePreferencesModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    preferences: {
      type: Array,
      default: () => []
    },
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'close', 'save', 'accept-all'],
  data() {
    return {
      statisticsEnabled: false,
      marketingEnabled: false
    }
  },
  computed: {
    isStatisticsEnabled() {
      return this.statisticsEnabled;
    },
    isMarketingEnabled() {
      return this.marketingEnabled;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        // Update toggle switches based on selected values
        this.updateTogglesFromPreferences(newVal);
      }
    },
    visible(newVal) {
      if (newVal) {
        document.body.classList.add('overflow-hidden');
        // Update toggles when modal becomes visible
        this.updateTogglesFromPreferences(this.modelValue);
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    }
  },
  methods: {
    updateTogglesFromPreferences(preferences) {
      // Check if statistics cookie is enabled
      this.statisticsEnabled = preferences.includes('statistics');
      
      // Check if marketing cookie is enabled
      this.marketingEnabled = preferences.includes('marketing');
    },
    getSelectedPreferences() {
      const selected = ['technical']; // Technical is always included (required)
      
      if (this.statisticsEnabled) {
        selected.push('statistics');
      }
      
      if (this.marketingEnabled) {
        selected.push('marketing');
      }
      
      return selected;
    },
    close() {
      this.$emit('close');
    },
    savePreferences() {
      const selectedPreferences = this.getSelectedPreferences();
      this.$emit('update:modelValue', selectedPreferences);
      this.$emit('save', selectedPreferences);
    },
    acceptAll() {
      this.statisticsEnabled = true;
      this.marketingEnabled = true;
      const allPreferences = ['technical', 'statistics', 'marketing'];
      this.$emit('update:modelValue', allPreferences);
      this.$emit('accept-all', allPreferences);
    }
  }
}
</script>